import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, Tr, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { BsThreeDots } from "react-icons/bs";

// Components
import CustomTable from "../../../components/CustomTable";
import CustomModal from "../../../components/customModal";
import FullPageLoader from "../../../components/full-page-loader";

// Services
import { getAllCheckouts, getSingleCheckout } from "./service";

// Constants
const CHECKOUT_HEAD = [
  "S/N",
  "CODE",
  "NAME",
  "TYPE",
  "STATUS",
  "ACTION"
];

// Detail View Modal Component
const CheckoutDetailModal = ({ data }) => {
  if (!data) return null;

  return (
    <Box p={4}>
      <Flex direction="column" gap={4}>
        <Flex justifyContent="space-between" borderBottom="1px solid #eee" pb={2}>
          <Text fontWeight="bold">Checkout Code:</Text>
          <Text>{data.type === 'errand' ? data.code : data.guestId?.guestCode}</Text>
        </Flex>
        
        <Flex justifyContent="space-between" borderBottom="1px solid #eee" pb={2}>
          <Text fontWeight="bold">Name:</Text>
          <Text>{data.name}</Text>
        </Flex>

        <Flex justifyContent="space-between" borderBottom="1px solid #eee" pb={2}>
          <Text fontWeight="bold">Items:</Text>
          <Text>{data.itemLists}</Text>
        </Flex>

        <Flex justifyContent="space-between" borderBottom="1px solid #eee" pb={2}>
          <Text fontWeight="bold">Type:</Text>
          <Text textTransform="capitalize">{data.type}</Text>
        </Flex>

        {data.type === 'guest' && (
          <Box borderBottom="1px solid #eee" pb={2}>
            <Text fontWeight="bold" mb={2}>Guest Information:</Text>
            <Flex direction="column" gap={2} pl={4}>
              <Flex justifyContent="space-between">
                <Text fontWeight="medium">Name:</Text>
                <Text>{data.guestId?.name || 'N/A'}</Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text fontWeight="medium">Code:</Text>
                <Text>{data.guestId?.guestCode || 'N/A'}</Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text fontWeight="medium">Status:</Text>
                <Text>{data.guestId?.status || 'N/A'}</Text>
              </Flex>
              {data.guestId?.checkInDate && (
                <Flex justifyContent="space-between">
                  <Text fontWeight="medium">Check-in Date:</Text>
                  <Text>{new Date(data.guestId.checkInDate).toLocaleDateString()}</Text>
                </Flex>
              )}
            </Flex>
          </Box>
        )}

        <Flex justifyContent="space-between" borderBottom="1px solid #eee" pb={2}>
          <Text fontWeight="bold">Created At:</Text>
          <Text>{new Date(data.createdAt).toLocaleString()}</Text>
        </Flex>

        <Flex justifyContent="space-between" borderBottom="1px solid #eee" pb={2}>
          <Text fontWeight="bold">Last Updated:</Text>
          <Text>{new Date(data.updatedAt).toLocaleString()}</Text>
        </Flex>
      </Flex>
    </Box>
  );
};

const ItemCheckout = () => {
  const userId = useSelector((state) => state.auth.user);
  
  // State
  const [state, setState] = useState({
    loading: true,
    filter: {},
    checkouts: [],
    selectedCheckout: null,
    page: 0,
    total: 0
  });

  useEffect(() => {
    getAllCheckouts(
      (loading) => setState(prev => ({ ...prev, loading })),
      (checkouts) => setState(prev => ({ 
        ...prev, 
        checkouts: checkouts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) 
      })),
      (total) => setState(prev => ({ ...prev, total })),
    );
  }, [userId, state.page]);

  if (state.loading) return <FullPageLoader />;

  return (
    <Box margin="20px" mt="60px">
      {/* Header Section */}
      <Flex
        bg="#fff"
        borderRadius="10px"
        padding="20px"
        mb="60px"
        justifyContent="space-between"
        flexDir={["column", "column", "row"]}
        width="100%"
        gap={["20px", "20px", "50px"]}
        alignItems="center"
      >
        <Box width={["100%"]}>
          <CustomTable.SearchInputField 
            setFilter={filter => setState(prev => ({ ...prev, filter }))} 
          />
        </Box>
      </Flex>

      {/* Table Section */}
      <Box>
        <CustomTable
          filter={state.filter}
          DBdata={state.checkouts}
          DBsearchID={["name", "code", "type"]}
          head={CHECKOUT_HEAD}
        >
          {(data) => data.map((row, idx) => (
            <Tr key={row._id}>
              <CustomTable.Td>
                <Text {...CustomTable.style}>{idx + 1}</Text>
              </CustomTable.Td>
              <CustomTable.Td>
                <CustomTable.LimitedText limit={15} {...CustomTable.style}>
                  {row.type === 'errand' ? row.code : row.guestId?.guestCode || "N/A"}
                </CustomTable.LimitedText>
              </CustomTable.Td>
              <CustomTable.Td>
                <CustomTable.LimitedText limit={15} {...CustomTable.style}>
                  {row?.name || "N/A"}
                </CustomTable.LimitedText>
              </CustomTable.Td>
              <CustomTable.Td>
                <CustomTable.LimitedText limit={15} {...CustomTable.style}>
                  {row?.type || "N/A"}
                </CustomTable.LimitedText>
              </CustomTable.Td>
              <CustomTable.Td>
                <CustomTable.LimitedText limit={15} {...CustomTable.style}>
                  {row?.guestId?.status || "N/A"}
                </CustomTable.LimitedText>
              </CustomTable.Td>
              <CustomTable.Td>
                <Menu>
                  <MenuButton>
                    <BsThreeDots size={24} color="#072f64" />
                  </MenuButton>
                  <MenuList pb="10px" bg="white" color="black">
                    <MenuItem>
                      <CustomModal
                        title="Checkout Details"
                        buttonText="View Details"
                        color="#000"
                        bg="transparent"
                        fontWeight="normal"
                      >
                        <CheckoutDetailModal data={row} />
                      </CustomModal>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </CustomTable.Td>
            </Tr>
          ))}
        </CustomTable>
      </Box>

      <CustomTable.Pagination
        length={state.checkouts?.length}
        total={state.total}
        updateTable={(page) => setState(prev => ({ ...prev, page }))}
      />
    </Box>
  );
};

export default ItemCheckout;